import { News } from '@/src/data/types';
import { BookOpen, Edit, Eye, FileEdit, MoreHorizontal, Trash } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { deleteNews, toggleDraftNews } from '../../../API/News/newsService';
import { AlertModal } from '../../modal/alert-modal';

const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;

interface CellActionProps {
  data: News;
}

export const CellAction: React.FC<CellActionProps> = ({ data }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const navigate = useNavigate();

  const onConfirmDelete = async () => {
    await deleteNews(data.id);
    setTimeout(() => {
      navigate(0);
    }, 1500);
  };

  const onToggleDraft = async () => {
    setToggling(true);
    await toggleDraftNews(data.id);
    setToggling(false);
    // Rafraîchir la table
    setTimeout(() => {
      navigate(0);
    }, 1500);
  };

  return (
    <>
      <AlertModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
      />

      {/* Modal aperçu */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu — {data.title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {data.isDraft && (
              <div className="mb-4 p-2 bg-yellow-100 border border-yellow-400 rounded text-yellow-800 text-sm">
                ⚠️ Cette actualité est en <strong>brouillon</strong> — elle n'est pas visible
                publiquement.
              </div>
            )}

            <h1 className="text-2xl font-bold text-black">{data.title}</h1>

            {data.date && (
              <p className="text-gray-500 mt-1">
                {new Date(data.date).toLocaleDateString('fr-FR', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
            <hr className="w-full h-1 mt-2 bg-primary" />

            {data.image && (
              <div className="mt-4">
                <img
                  src={`${uploadUrl}/news/${data.image}`}
                  alt={data.title}
                  className="w-full rounded-md"
                />
              </div>
            )}

            {data.description && (
              <div
                className="mt-6 prose max-w-none"
                dangerouslySetInnerHTML={{ __html: data.description }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="w-8 h-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>

          {/* Aperçu */}
          <DropdownMenuItem onClick={() => setPreviewOpen(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Aperçu
          </DropdownMenuItem>

          {/* Modifier */}
          <DropdownMenuItem onClick={() => navigate(`/admin-dashboard/actualité/${data.id}`)}>
            <Edit className="w-4 h-4 mr-2" />
            Modifier
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Toggle brouillon / publier */}
          {/* <DropdownMenuItem
            disabled={toggling}
            onClick={onToggleDraft}
            className={
              data.isDraft
                ? "text-green-700 focus:text-green-700"
                : "text-yellow-700 focus:text-yellow-700"
            }
          >
            {data.isDraft ? (
              <>
                <BookOpen className="w-4 h-4 mr-2" />
                Publier
              </>
            ) : (
              <>
                <FileEdit className="w-4 h-4 mr-2" />
                Mettre en brouillon
              </>
            )}
          </DropdownMenuItem> */}

          <DropdownMenuSeparator />

          {/* Supprimer */}
          <DropdownMenuItem
            onClick={() => setDeleteOpen(true)}
            className="text-red-600 focus:text-red-600"
          >
            <Trash className="w-4 h-4 mr-2" />
            Supprimer
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
